import React, { useCallback, useEffect, useMemo, useState } from "react";
import api, { brl } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Search, ShoppingCart } from "lucide-react";
import { ReceiptDialog } from "@/components/SaleReceipt";
import { LinearServiceDialog } from "@/components/LinearServiceDialog";
import { ProductGrid, CartItems, PaymentMethods, PaymentDetails, CustomerFields } from "@/components/PdvParts";
import { usePdvCart } from "@/hooks/usePdvCart";
import { isLinear } from "@/lib/linearPricing";

const matches = (p, category, search) => {
  if (!p.active) return false;
  if (category !== "all" && p.category !== category) return false;
  if (!search) return true;
  return p.name.toLowerCase().includes(search.toLowerCase()) || (p.barcode || "").includes(search);
};

const validateSale = (cart, payment, cashReceived, total, dueDate) => {
  if (cart.length === 0) return "Adicione itens ao carrinho";
  if (cart.some((i) => !(Number(i.quantity) > 0))) return "Informe uma quantidade válida para todos os itens";
  if (payment === "cash" && cashReceived && Number(cashReceived) < total) return "Valor recebido insuficiente";
  if (payment === "credit_term" && !dueDate) return "Informe o vencimento da promissória";
  return null;
};

const ProductFilters = ({ search, setSearch, category, setCategory, categories }) => (
  <div className="flex flex-col sm:flex-row gap-3">
    <div className="relative flex-1">
      <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
      <Input placeholder="Buscar produto ou código de barras..." className="pl-9"
        value={search} onChange={(e) => setSearch(e.target.value)}
        data-testid="product-search-input" autoFocus />
    </div>
    <Select value={category} onValueChange={setCategory}>
      <SelectTrigger className="w-full sm:w-52" data-testid="category-filter"><SelectValue placeholder="Categoria" /></SelectTrigger>
      <SelectContent>
        {categories.map((c) => <SelectItem key={c} value={c}>{c === "all" ? "Todas Categorias" : c}</SelectItem>)}
      </SelectContent>
    </Select>
  </div>
);

const Totals = ({ subtotal, discount, setDiscount, total }) => (
  <div className="space-y-2 border-t pt-3">
    <div className="flex justify-between text-sm"><span className="text-slate-500">Subtotal</span><span className="kpi-num">{brl(subtotal)}</span></div>
    <div className="flex items-center gap-2">
      <Label className="text-sm text-slate-500 flex-1">Desconto R$</Label>
      <Input type="number" step="0.01" value={discount} onChange={(e) => setDiscount(e.target.value)} className="w-24 text-right" data-testid="discount-input" />
    </div>
    <div className="flex justify-between text-lg font-semibold border-t pt-2">
      <span>Total</span><span className="kpi-num text-blue-600">{brl(total)}</span>
    </div>
  </div>
);

export default function PDV() {
  const [products, setProducts] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [search, setSearch] = useState("");
  const [category, setCategory] = useState("all");
  const [discount, setDiscount] = useState(0);
  const [payment, setPayment] = useState("cash");
  const [cashReceived, setCashReceived] = useState("");
  const [customer, setCustomer] = useState("");
  const [customerId, setCustomerId] = useState("");
  const [dueDate, setDueDate] = useState("");
  const [installments, setInstallments] = useState(1);
  const [lastSale, setLastSale] = useState(null);
  const [linearProduct, setLinearProduct] = useState(null);
  const { cart, setCart, addProduct, addLinearLine, subtotal, payloadItems, handlers } = usePdvCart();

  const load = useCallback(() => {
    api.get("/products").then((r) => setProducts(r.data));
    api.get("/customers").then((r) => setCustomers(r.data)).catch(() => setCustomers([]));
  }, []);
  useEffect(() => { load(); }, [load]);

  const categories = useMemo(() => ["all", ...new Set(products.map((p) => p.category).filter(Boolean))], [products]);
  const filtered = useMemo(() => products.filter((p) => matches(p, category, search)), [products, search, category]);

  const total = Math.max(0, subtotal - (Number(discount) || 0));
  const change = payment === "cash" && cashReceived ? Math.max(0, Number(cashReceived) - total) : 0;

  const addToCart = (p) => (isLinear(p) ? setLinearProduct(p) : addProduct(p));
  const addLinear = ({ meters, total: t }) => { addLinearLine(linearProduct, meters, t); setLinearProduct(null); };

  const resetSale = () => {
    setCart([]); setDiscount(0); setCashReceived(""); setCustomer(""); setCustomerId("");
    setDueDate(""); setInstallments(1);
  };

  const finish = async () => {
    const err = validateSale(cart, payment, cashReceived, total, dueDate);
    if (err) return toast.error(err);
    const term = payment === "credit_term";
    try {
      const { data } = await api.post("/sales", {
        items: payloadItems(), discount: Number(discount) || 0, payment_method: payment,
        cash_received: payment === "cash" ? Number(cashReceived) || null : null,
        customer: customer || (customerId ? customers.find((c) => c.id === customerId)?.name : null),
        customer_id: customerId || null,
        due_date: term ? dueDate : null,
        installments: term ? Math.max(1, Number(installments) || 1) : 1,
      });
      setLastSale(data);
      resetSale();
      toast.success("Venda finalizada!");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao finalizar venda");
    }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-5 gap-4 min-h-[calc(100vh-4rem)]" data-testid="pdv-page">
      <div className="lg:col-span-3 space-y-4">
        <div>
          <div className="text-xs uppercase tracking-widest text-slate-500 mb-1">Ponto de Venda</div>
          <h1 className="font-display text-3xl font-extrabold tracking-tight">PDV — Caixa Rápido</h1>
        </div>
        <ProductFilters search={search} setSearch={setSearch} category={category} setCategory={setCategory} categories={categories} />
        <ProductGrid products={filtered} onAdd={addToCart} />
      </div>

      <div className="lg:col-span-2">
        <Card className="card-outline sticky top-4">
          <CardContent className="p-5 space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="font-display text-xl font-bold flex items-center gap-2"><ShoppingCart className="w-5 h-5" /> Carrinho</h2>
              <Badge variant="secondary">{cart.length} itens</Badge>
            </div>
            <CartItems cart={cart} {...handlers} />
            <Totals subtotal={subtotal} discount={discount} setDiscount={setDiscount} total={total} />
            <PaymentMethods payment={payment} setPayment={setPayment} />
            <PaymentDetails payment={payment} f={{ dueDate, setDueDate, installments, setInstallments, cashReceived, setCashReceived, change }} />
            <CustomerFields customers={customers} customer={customer} setCustomer={setCustomer} customerId={customerId} setCustomerId={setCustomerId} />
            <Button className="w-full bg-blue-600 hover:bg-blue-700 h-12 text-base" onClick={finish} data-testid="finish-sale-btn">
              Finalizar Venda • {brl(total)}
            </Button>
          </CardContent>
        </Card>
      </div>

      <ReceiptDialog sale={lastSale} onClose={() => setLastSale(null)} />
      <LinearServiceDialog product={linearProduct} onClose={() => setLinearProduct(null)} onConfirm={addLinear} />
    </div>
  );
}
